import type {AiImageAspectRatioOptions, AiImagePanelProps} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AI_IMAGE_FOOTER_OPTIONS_PANEL_ID} from '@Panels/ai-image-panel/ai-image-panel.types';
import type {ElementData} from '@Libraries/add-media-library';
import type {MouseEventHandler, ReactElement} from 'react';
import {useEffect, useRef} from 'react';
import type {PanelFooterProps} from '@Components/panel';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import {Icon} from '@Components/icon-v2';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {openMoreOptionsModal} from '@Modals/more-options-modal/more-options-modal-library';
import type {PanelFooterSecondaryActionType} from '@Components/panel/components/panel-footer-secondary-actions/panel-footer-secondary-actions';
import {copyToClipboard} from '@Utils/clipboard.util';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelFooterSecondaryCustomAction, PanelHeader} from '@Components/panel';
import type {AITextToImageBackendData} from '@Libraries/pmw-stock-media-library';
import {setRecentStockItem} from '@Libraries/pmw-stock-media-library';
import {v4 as uuidv4} from 'uuid';
import {addItemsToGrid, deleteGridItems, getSelectedGridItems, replaceGridItem, toggleGridItemSelection, updateGridItem} from '@Components/base-grid';
import {getGridItemForAIGeneratedImageV2, isAiEditedImageGridItem, shouldAiPanelCloseOnItemAdd, uploadSelectedItem} from '@Panels/stock-panel/stock-panel.library';
import {TextToImageGrid} from '@Components/text-to-image-grid';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {MIN_STOCK_ITEMS_TO_DISPLAY} from '@Panels/stock-panel/stock-panel.types';
import type {GridAiImageItemStorage} from '@Components/base-grid/components/grid-stock-image-item';
import {isUserPremium} from '@Libraries/user.library';
import {confirmAndDeleteGeneratedImage, getGeneratedImageMenuItems, getGridIdForPanel} from '@Libraries/text-to-image-library';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {MODAL_IDS, openModal} from '@Components/modal-container-deprecated';
import {setRecentStockAvailability} from '@Panels/stock-panel/stock-panel-reducer';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {isMobile} from 'react-device-detect';
import type {TextToImageGridProps} from '@Components/text-to-image-grid/text-to-image-grid.types';
import type {AiImageGenerationState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import {AiImageState} from '@Components/poster-editor/components/poster-ai/poster-ai.types';
import styles from './ai-image-panel.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {addAiImageGenerationItem, removeAiImageGenerationItem, updateIsUploadingToMyUploads} from '@/components/poster-editor/poster-editor-reducer';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import AiImagePromptBox from '@Panels/ai-image-panel/components/ai-image-prompt-box/ai-image-prompt-box';
import {
  AI_IMAGE_GENERATION_SKELETON_ID_PREFIX,
  getSkeletonGridItemsForAiImageGeneration,
  getSkeletonIdsForAiImageGeneration,
} from '@Panels/ai-image-panel/ai-image-panel-library';
import {AiImageUpsell} from '@Panels/ai-image-panel/components/ai-image-upsell/ai-image-upsell';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import type {CanvasDropPosition} from '@Libraries/canvas-item-drag.library';
import {createGridItemDragHandler, placeItemAtDropPosition} from '@Libraries/canvas-item-drag.library';
import {GeneratedImageItemMenu} from '@Components/text-to-image-grid/components/generated-image-item-menu/generated-image-item-menu';
import {openEditWithAiForActiveImage} from '@Libraries/text-to-image-library';

const SELECTION_ID = 'TEXT_TO_IMAGE_POP_OVER_SELECTION';
const PANEL_ID = 'TEXT_TO_IMAGE_PANEL_FALLBACK_ID';

export default function AiImagePanel({
  populateRecentlyAddedOnItemAdd = true,
  showBackButton = true,
  showInPanelUpsell = false,
  enableDragToCanvas = false,
  showEditWithAiOnGeneratedImages = false,
  addInBulk = false,
  enableMultiSelection = false,
  ...props
}: AiImagePanelProps): ReactElement {
  const isUpsellShown = showInPanelUpsell && !isUserPremium();
  const isInMultiSelectionMode = isMobile || enableMultiSelection;
  const selectionIDRef = useRef(props.selectionID ?? SELECTION_ID);
  const gridItemIdsProcessingRef = useRef<Record<string, true>>({});
  const selectedItems = useAppSelector((state) => {
    return getSelectedGridItems(state, selectionIDRef.current);
  });
  const aiImageState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImage;
  });
  const aiImageGenerations = useAppSelector((state) => {
    return state.posterEditor.aiState.aiImageGenerations;
  });
  const dispatch = useAppDispatch();
  const panelId = useRef(props.panelId ?? PANEL_ID);
  const gridIDRef = useRef(getGridIdForPanel(panelId.current));
  const gridRef = useRef<HTMLDivElement | null>(null);
  const {topShadow, bottomShadow} = useTopBottomScrollShadow(gridRef, !!gridRef.current);
  const closeFooterOptionsModal = useDefaultPanelClose(AI_IMAGE_FOOTER_OPTIONS_PANEL_ID);
  const onClose: () => void = props.onClose
    ? (): void => {
        if (props.onClose) {
          props.onClose();
        }
      }
    : useDefaultPanelClose(panelId.current);

  useEffect(() => {
    const gridItems = window.PMW.redux.store.getState().grids.gridsHashmap[gridIDRef.current]?.items ?? [];
    const gridItemIds = new Set(gridItems.map((item) => item.id));

    const skeletonItemsToAdd = aiImageGenerations.flatMap(getSkeletonGridItemsForAiImageGeneration).filter((item) => !gridItemIds.has(item.id));
    if (skeletonItemsToAdd.length > 0) {
      dispatch(
        addItemsToGrid({
          id: gridIDRef.current,
          items: skeletonItemsToAdd,
          index: 0,
        })
      );
    }

    const generatingSkeletonIds = new Set(aiImageGenerations.flatMap(getSkeletonIdsForAiImageGeneration));
    const staleSkeletonIds = gridItems
      .filter((item) => typeof item.id === 'string' && item.id.startsWith(AI_IMAGE_GENERATION_SKELETON_ID_PREFIX) && !generatingSkeletonIds.has(item.id))
      .map((item) => item.id);
    if (staleSkeletonIds.length > 0) {
      dispatch(
        deleteGridItems({
          gridId: gridIDRef.current,
          gridSelectionGroupId: selectionIDRef.current,
          itemIdsToDelete: staleSkeletonIds,
        })
      );
    }
  }, [aiImageGenerations, dispatch]);

  const onGridItemClicked = (item: GridItemStorage, isDoubleClick: boolean): void => {
    if (isInMultiSelectionMode) {
      if (isDoubleClick && selectedItems.length === 1) {
        void onImageClicked([item]);
      } else {
        dispatch(
          toggleGridItemSelection({
            gridItemId: item.id,
            gridSelectionGroupId: selectionIDRef.current,
          })
        );
      }
    } else {
      void onImageClicked([item]);
    }
  };

  const onEditWithAiSelected = async (item: AITextToImageBackendData): Promise<void> => {
    onClose();
    await onImageClicked([getGridItemForAIGeneratedImageV2(item)]);
    openEditWithAiForActiveImage();
  };

  const getItemTopRightContent = (item: AITextToImageBackendData): ReactElement | undefined => {
    if (isMobile) {
      return undefined;
    }
    return (
      <GeneratedImageItemMenu
        item={item}
        gridId={gridIDRef.current}
        gridSelectionGroupId={selectionIDRef.current}
        onEditWithAi={
          showEditWithAiOnGeneratedImages
            ? (itemToEdit): void => {
                void onEditWithAiSelected(itemToEdit);
              }
            : undefined
        }
      />
    );
  };

  const getAIImageGrid = (): ReactElement => {
    const gridProps: TextToImageGridProps = {
      customOnGridItemClicked: onGridItemClicked,
      gridClasses: `spacing-p-t-6 spacing-p-b-4 ${styles.grid}`,
      gridSelectionGroupId: selectionIDRef.current,
      maxSelectionNumber: isInMultiSelectionMode ? props.maxSelectionNumber : 1,
      gridId: gridIDRef.current,
      makeContentScrollable: false,
      isLoading: aiImageState === AiImageState.GENERATING,
      isItemDraggable: enableDragToCanvas,
      onItemPointerDown,
      getItemTopRightContent,
    };

    return <TextToImageGrid {...gridProps} key={gridIDRef.current} />;
  };

  const getPromptingContent = (): ReactElement => {
    if (isUpsellShown) {
      return <AiImageUpsell />;
    }

    return (
      <>
        <AiImagePromptBox
          onImageGenerationStarted={addLoadingSkeletonsToGrid}
          onImageGenerationSuccessful={onGeneratedImagesReceived}
          onImageGenerationFailed={removeLoadingSkeletonsFromGrid}
          mediaType={props.mediaType}
          alignSettingsPopupWithFooter={props.isOpenedInPopOver}
        />
        {getAIImageGrid()}
      </>
    );
  };

  const getPanelContent = (): ReactElement<HTMLDivElement> => {
    return <div className={'spacing-p-l-4 spacing-p-r-4'}>{getPromptingContent()}</div>;
  };

  const onBackButton: MouseEventHandler = (e) => {
    if (props.onBack) {
      props.onBack(e);
    }
  };

  const shouldShowHeader = (): boolean => {
    if (isMobile) {
      return true;
    }
    return !!props.onBack;
  };

  const shouldShowHeaderBorder = (): boolean => {
    return topShadow;
  };

  const onItemPointerDown = createGridItemDragHandler<GridItemStorage>((item, dropPosition) => onImageClicked([item], dropPosition));

  const onImageClicked = async (items: GridItemStorage[], dropPosition?: CanvasDropPosition): Promise<void> => {
    if (enableMultiSelection || shouldAiPanelCloseOnItemAdd()) {
      onClose();
    }
    showLoading('addStockMediaItem', {
      text: items.length > 1 ? window.i18next.t('pmwjs_adding') : window.i18next.t('pmwjs_adding_1_of_1'),
    });

    let recentStockItemCount: number | null = null;
    const bulkUploadedItems: ElementData[] = [];

    for (const item of items) {
      if (item.id in gridItemIdsProcessingRef.current) {
        continue;
      }

      gridItemIdsProcessingRef.current[item.id] = true;
      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: true,
          },
        })
      );
      try {
        dispatch(updateIsUploadingToMyUploads(true));

        const onAddItemResponse = await uploadSelectedItem(item, props.shouldLoadDataUrlImage);
        dispatch(updateIsUploadingToMyUploads(false));
        trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_ADDED);
        const uploadedItem = placeItemAtDropPosition(onAddItemResponse, dropPosition);
        if (addInBulk) {
          bulkUploadedItems.push(uploadedItem);
        } else {
          await props.onAddSelected([uploadedItem]);
        }

        if (populateRecentlyAddedOnItemAdd && isUserPremium() && !isAiEditedImageGridItem(item)) {
          recentStockItemCount = await setRecentStockItem(item);
        }
      } catch (e) {
        console.error(e);

        openModal(MODAL_IDS.ERROR_MODAL_ID, {
          message: window.i18next.t('pmwjs_general_ajax_error'),
          log: false,
        });
      }

      dispatch(
        updateGridItem({
          gridId: gridIDRef.current,
          itemId: item.id,
          changes: {
            isLoading: false,
          },
        })
      );

      delete gridItemIdsProcessingRef.current[item.id];
    }

    if (bulkUploadedItems.length > 0) {
      await props.onAddSelected(bulkUploadedItems);
    }

    if (recentStockItemCount !== null) {
      dispatch(setRecentStockAvailability(recentStockItemCount >= MIN_STOCK_ITEMS_TO_DISPLAY));
    }
    hideLoading('addStockMediaItem');
  };

  const onAddSelectedClicked = (): void => {
    void onImageClicked(isInMultiSelectionMode ? selectedItems : [selectedItems[0]]);
  };

  const getPrimaryAction = (): ReactElement<any>[] => {
    return [<PanelFooterPrimaryDefaultAction key="stock-btn-cta-footer" text={window.i18next.t('pmwjs_add_selected')} onClick={onAddSelectedClicked} />];
  };

  const getStretchedPrimaryAction = (): ReactElement<any>[] => {
    return [<PanelFooterPrimaryDefaultAction key="stock-btn-cta-footer" className="_full-width" text={window.i18next.t('pmwjs_add_selected')} onClick={onAddSelectedClicked} />];
  };

  const getSelectedItemBackendData = (): AITextToImageBackendData | null => {
    if (selectedItems.length !== 1) {
      return null;
    }
    const selectedItem = selectedItems[0];
    if (selectedItem.type !== GRID_ITEM_TYPE.STOCK_IMAGE) {
      return null;
    }
    return selectedItem.backendData ?? null;
  };

  const getFooterOptionsMenuItems = (item: AITextToImageBackendData): ControlledListItemType[] => {
    return getGeneratedImageMenuItems({
      onEditWithAi: showEditWithAiOnGeneratedImages
        ? (): void => {
            closeFooterOptionsModal();
            void onEditWithAiSelected(item);
          }
        : undefined,
      onCopyPrompt: (): void => {
        closeFooterOptionsModal();
        void copyToClipboard(item.prompt.text);
        showMessageGrowl({text: window.i18next.t('pmwjs_prompt_copied'), type: GROWL_TYPE.SUCCESS});
      },
      onDelete: (): void => {
        closeFooterOptionsModal();
        confirmAndDeleteGeneratedImage(item, gridIDRef.current, selectionIDRef.current);
      },
    });
  };

  const openFooterOptionsModal = (item: AITextToImageBackendData): void => {
    openMoreOptionsModal({
      panelId: AI_IMAGE_FOOTER_OPTIONS_PANEL_ID,
      headerTitle: window.i18next.t('pmwjs_options'),
      isSmallScreenModalBottomSheet: true,
      shouldCenterAlignTitle: true,
      content: <ControlledList className="spacing-m-0 spacing-p-4" items={getFooterOptionsMenuItems(item)} />,
    });
  };

  const getFooterSecondaryActions = (item: AITextToImageBackendData): PanelFooterSecondaryActionType[] => {
    return [
      <PanelFooterSecondaryCustomAction key="btn-generated-image-options">
        <div className="flex-center">
          <Icon
            icon="icon-menu-dots"
            size={IconSize.SIZE_ICON_20}
            shape={IconShape.SQUARE}
            isUsedAsButton
            ariaLabel={window.i18next.t('pmwjs_options')}
            onClick={(): void => {
              openFooterOptionsModal(item);
            }}
          />
        </div>
      </PanelFooterSecondaryCustomAction>,
    ];
  };

  const getOldestPendingGeneration = (): AiImageGenerationState | undefined => {
    return window.PMW.redux.store.getState().posterEditor.aiState.aiImageGenerations[0];
  };

  const onGeneratedImagesReceived = (response: AITextToImageBackendData[]): void => {
    const generation = getOldestPendingGeneration();
    if (!generation) {
      return;
    }
    const skeletonIds = getSkeletonIdsForAiImageGeneration(generation);
    const items: GridAiImageItemStorage[] = response.map((backendItem) => ({
      ...getGridItemForAIGeneratedImageV2(backendItem),
      topRightContent: getItemTopRightContent(backendItem),
    }));
    items.slice(0, skeletonIds.length).forEach((gridItem, index): void => {
      dispatch(replaceGridItem({gridId: gridIDRef.current, itemId: skeletonIds[index], gridItem}));
    });
    const itemsBeyondSkeletons = items.slice(skeletonIds.length);
    if (itemsBeyondSkeletons.length > 0) {
      dispatch(addItemsToGrid({id: gridIDRef.current, items: itemsBeyondSkeletons, index: skeletonIds.length}));
    }
    dispatch(removeAiImageGenerationItem(generation.id));
  };

  const addLoadingSkeletonsToGrid = (aspectRatio: AiImageAspectRatioOptions, imageCount: number): void => {
    dispatch(addAiImageGenerationItem({id: uuidv4(), aspectRatio, imageCount}));
  };

  const removeLoadingSkeletonsFromGrid = (): void => {
    const generation = getOldestPendingGeneration();
    if (generation) {
      dispatch(removeAiImageGenerationItem(generation.id));
    }
  };

  const getPanelFooter = (): ReactElement<PanelFooterProps> | undefined => {
    if (isUpsellShown || !selectedItems.length) {
      return undefined;
    }

    const selectedItemBackendData = isMobile ? getSelectedItemBackendData() : null;
    if (selectedItemBackendData) {
      return (
        <PanelFooter
          key={selectionIDRef.current}
          primaryActions={getStretchedPrimaryAction()}
          primaryActionsClassName="flex-1"
          secondaryActions={getFooterSecondaryActions(selectedItemBackendData)}
          border
        />
      );
    }

    return <PanelFooter key={selectionIDRef.current} primaryActions={getPrimaryAction()} border fullWidthPrimaryActions />;
  };

  return (
    <Panel
      className={`_full-width _full-height ${props.className ?? ''}`}
      panelHeader={
        shouldShowHeader() ? (
          <PanelHeader
            backButtonIconSize={!isMobile ? IconSize.SIZE_ICON_16 : undefined}
            titleClasses={'_fit-width content-ai body-md-bold'}
            className={`${shouldShowHeaderBorder() ? '' : '_borderless'} ${!isMobile ? 'spacing-p-l-4 spacing-p-r-4' : ''}`}
            title={window.i18next.t('pmwjs_ai_images')}
            showCloseButton={isMobile}
            onClose={onClose}
            showBackButton={showBackButton}
            onBackButton={onBackButton}
            forceCenterAlignTitleWithActionItems
            isOpenedInModal={isMobile}
          />
        ) : undefined
      }
      panelContent={
        <PanelContent className={`_full-width flex-v-row ${styles.scrollShadowHost} ${topShadow ? styles.topShadow : ''} ${bottomShadow ? styles.bottomShadow : ''}`}>
          <div ref={gridRef} className={`flex-v-row ${styles.panelContent}`}>
            {getPanelContent()}
          </div>
        </PanelContent>
      }
      panelFooter={getPanelFooter()}
    />
  );
}
